import React, { useContext } from 'react';
import { Button, Modal } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { useGetPlansCultureActionsQuery, useDeletePlansCultureActionMutation } from '@/areas/culture-hub/api/plansCultureActions';
import { StyledAddButton, StyledTrashButton } from '@/common/components/button/StyledButton';
import MobileCultureActionEditor from './MobileCultureActionEditor';
import { MobileNavButtonDescriptor, TopBarContext } from '@/common/layouts/AuthLayout/TopBarContext';
import { MobilePageContent, MobilePageSection, BodyText, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';
import { getInvolvedLabel } from '@/areas/culture-hub/components/interventions/culture-actions/involvedHelpers';
import styled from 'styled-components';

const IMPACT_LEVELS = [
  { value: 0, label: 'None', color: '#dcfce7' },
  { value: 1, label: 'Low', color: '#86efac' },
  { value: 2, label: 'Medium', color: '#4ade80' },
  { value: 3, label: 'High', color: '#16a34a' },
  { value: 4, label: 'Huge', color: '#065f46' },
];

interface MobileCultureActionsProps {
  actionPlanId?: number | null;
  onEditorOpen: (isOpen: boolean) => void;
}

const MobileCultureActions: React.FC<MobileCultureActionsProps> = ({ actionPlanId, onEditorOpen }) => {
  const { data, isLoading } = useGetPlansCultureActionsQuery(
    { assessmentPeriodActionPlanId: actionPlanId as number },
    { skip: !actionPlanId }
  );
  const [deleteAction] = useDeletePlansCultureActionMutation();
  const { addToast } = useToasts();
  const { setNavButton } = useContext(TopBarContext);

  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingItem, setPendingItem] = React.useState<{ itemId: number; title?: string } | null>(null);

  const openForCreate = () => {
    setEditing(null);
    setIsEditorOpen(true);
    onEditorOpen(true);
  };

  const openForEdit = (item: any) => {
    setEditing(item);
    setIsEditorOpen(true);
    onEditorOpen(true);
  };

  const handleRequestDelete = (item: any) => {
    setPendingItem({ itemId: item.id, title: item.action_taken || 'this action' });
    setConfirmOpen(true);
  };

  React.useEffect(() => {
    if (isEditorOpen) {
      const closeNavButton: MobileNavButtonDescriptor = {
        type: 'close',
        onClick: () => {
          setIsEditorOpen(false);
          onEditorOpen(false);
        },
      };
      setNavButton(closeNavButton);
    } else {
      setNavButton(null);
    }

    return () => {
      setNavButton(null);
    };
  }, [isEditorOpen, setNavButton, onEditorOpen]);

  const handleConfirmDelete = async () => {
    if (!pendingItem) {
      return;
    }

    try {
      await deleteAction({ id: pendingItem.itemId }).unwrap();
      addToast('Culture action deleted', { appearance: 'success', autoDismiss: true });
    } catch (err: any) {
      console.error('Failed to delete culture action:', err);
      addToast('Failed to delete culture action', { appearance: 'error', autoDismiss: true });
    }

    setConfirmOpen(false);
    setPendingItem(null);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingItem(null);
  };

  const renderImpactLabel = (impact?: number) => {
    const idx = Math.min(Math.max(Number(impact ?? 0), 0), IMPACT_LEVELS.length - 1);
    const level = IMPACT_LEVELS[idx];
    return (
      <MetaPill style={{ background: level.color, color: '#065f46' }}>
        {level.label} impact
      </MetaPill>
    );
  };

  if (isEditorOpen) {
    return (
      <MobileCultureActionEditor
        onClose={() => {
          setIsEditorOpen(false);
          onEditorOpen(false);
        }}
        actionPlanId={actionPlanId ?? undefined}
        initial={editing}
      />
    );
  }

  return (
    <MobilePageContent>
      <MobilePageSection padbottom={'20px'}>
        <HeaderRow>
          <StyledAddButton label="Log culture action" onClick={openForCreate} disabled={!actionPlanId} width="100%" height="42px" />
        </HeaderRow>
      </MobilePageSection>

      <MobilePageSection>
        {!actionPlanId ? (
          <BodyText>No action plan selected. Create or select an action plan before adding culture actions.</BodyText>
        ) : isLoading ? (
          <MobileSpinner text="Loading action entries..." size="small" />
        ) : !data || data.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>No culture action entries yet.</EmptyStateTitle>
            <BodyText>Add a new action entry to track what happened, who was involved, and what happens next.</BodyText>
          </EmptyState>
        ) : (
          <ActionsList>
            {data.map((entry: any) => {
              const involved = getInvolvedLabel(entry.involved_party);
              const impactElement = renderImpactLabel(entry.impact);

              return (
                <ActionCard
                  key={entry.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openForEdit(entry)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      openForEdit(entry);
                    }
                  }}
                >
                  <ActionCardHeader>
                    <ActionTitle>{entry.action_taken || 'New culture action'}</ActionTitle>
                    <StyledTrashButton
                      id={entry.id}
                      onClick={() => handleRequestDelete(entry)}
                      title="Delete action"
                    />
                  </ActionCardHeader>

                  <MetaRow>
                    <MetaPill>{involved}</MetaPill>
                    {impactElement}
                  </MetaRow>

                  <ActionText>
                    <ActionLabel>Observations</ActionLabel>
                    <div>{entry.observations || 'No observations provided.'}</div>
                  </ActionText>

                  <ActionText>
                    <ActionLabel>Next Actions</ActionLabel>
                    <div>{entry.next_actions || 'No next actions listed.'}</div>
                  </ActionText>
                </ActionCard>
              );
            })}
          </ActionsList>
        )}
      </MobilePageSection>

      <Modal size="small" open={confirmOpen} onClose={handleCancelDelete} closeIcon>
        <Modal.Header>Remove action entry</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to remove <strong>{pendingItem?.title}</strong> from this focus area?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={handleCancelDelete}>Cancel</Button>
          <Button negative onClick={handleConfirmDelete}>Remove</Button>
        </Modal.Actions>
      </Modal>
    </MobilePageContent>
  );
};

export default MobileCultureActions;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;



const ActionsList = styled.div`
  display: grid;
  gap: 12px;
`;

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  }
`;

const ActionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const ActionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex: 1;
  min-width: 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #064e3b;
  background: #d1fae5;
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #475569;
  font-size: 13px;
`;

const ActionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #334155;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const EmptyStateTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;
